import React, { useState } from 'react';
import { Typography, Stack, Modal, Box, IconButton, Snackbar, Alert, Button, Tooltip } from '@mui/material';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpIcon from '@mui/icons-material/Help';
import CloseModalIcon from '../../icons/RoundCloseIcon.svg';
import UserRegistrationIcon from '../../icons/UserRegistrationIcon.svg';
import ResetPasswordIcon from '../../icons/ResetPasswordIcon.svg';
import { makeStyles } from '@mui/styles';
import SpinnerModal from '../../shared/Modal/SpinnerModal';
import { format } from 'date-fns';
import { SubjectDetailsPassReset } from './hooks/usePasswordResetConfigs';

const useStyles = makeStyles((theme) => ({
  globalError: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '30px',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '340px',
    height: '49px',
  },
  wrapper: {
    margin: '1px',
    position: 'relative',
  },
  buttonProgress: {
    color: '#053742',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  cardContainer: {
    width: '100% !important',
  },
}));

export const enum ResetPasscodeModalStates {
  UserRegistration = 'User Registration',
  ResetPassword = 'Reset Password',
}

export interface ResetPasswordModalProps {
  open: boolean;
  handleClose: () => void; // handle all cleanup tasks when modal is closed
  subjectDetails: SubjectDetailsPassReset;
  handleTempPasscode: (profileUuid: string, userUuid: string) => void;
  modalState: ResetPasscodeModalStates;
  setModalState: (value: React.SetStateAction<ResetPasscodeModalStates>) => void;
  tempPasscode: string;
  tempPasscodeError: string;
  isSpinnerModalOpen: boolean;
  setIsSpinnerModalOpen: (value: React.SetStateAction<boolean>) => void;
  spinnerStatus: string | null;
}

const ResetPasswordModal = (props: ResetPasswordModalProps) => {
  const classes = useStyles();
  const goToPrevState = () => {
    if (props.modalState === ResetPasscodeModalStates.ResetPassword) {
      props.setModalState(ResetPasscodeModalStates.UserRegistration);
    }
  };

  const [snackBarOpen, setSnackBarOpen] = useState(false);

  // SpinnerModal behavior && error handling: start
  const handleCloseSpinnerModal = () => {
    props.setIsSpinnerModalOpen(false);
  };

  return (
    <div>
      <Modal open={props.open} onClose={props.handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '520px',
            height: '555px',
            background: '#FFFFFF',
            boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
            borderRadius: '24px',
            p: 4,
            outline: 'none',
          }}
        >
          <Stack direction="column" justifyContent="start" alignItems="space-between" spacing={1}>
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ position: 'relative' }}>
              {props.modalState !== ResetPasscodeModalStates.UserRegistration && (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="go to prev"
                  onClick={goToPrevState}
                  sx={{
                    position: 'absolute',
                    left: 0,
                  }}
                >
                  <ArrowCircleLeftIcon sx={{ width: '30px', height: '30px', fill: '#39A2DB' }} />
                </IconButton>
              )}
              <Typography
                style={{
                  fontFamily: '"Work Sans",Sans serif',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  fontSize: '18px',
                  lineHeight: '22px',
                  color: '#14142B',
                  margin: '0 !important',
                }}
              >
                {props.modalState}
              </Typography>
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Close modal"
                onClick={props.handleClose}
                sx={{
                  position: 'absolute',
                  right: 0,
                }}
              >
                <img src={CloseModalIcon} alt="close" />
              </IconButton>
            </Stack>
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={1}
              sx={{
                minHeight: 420,
              }}
            >
              {props.modalState === ResetPasscodeModalStates.UserRegistration ? (
                <React.Fragment>
                  {props.tempPasscodeError?.length ? (
                    <div className={classes.globalError}>
                      <Typography
                        style={{
                          fontFamily: "'Open Sans', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '18px',
                          color: '#DB3939',
                        }}
                      >
                        {props.tempPasscodeError}
                      </Typography>
                    </div>
                  ) : (
                    ''
                  )}
                  <img src={UserRegistrationIcon} style={{ marginTop: '40px' }} alt="" />
                  <Typography
                    style={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '22px',
                      textAlign: 'center',
                      color: '#000000',
                      marginTop: '40px',
                    }}
                  >
                    {props.subjectDetails.Email}
                  </Typography>
                  <Typography
                    style={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '25px',
                      textAlign: 'center',
                      color: '#14142B',
                      marginTop: '31px',
                    }}
                  >
                    registered on: {props.subjectDetails.CreatedAt ? format(new Date(props.subjectDetails.CreatedAt), 'M/d/yyyy h:mma') : 'NA'}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => props.handleTempPasscode(props.subjectDetails.ProfileUuid, props.subjectDetails.UserUuid)}
                    style={{
                      width: '340px',
                      height: '49px',
                      borderRadius: '24px',
                      textTransform: 'capitalize',
                      color: 'white',
                      backgroundColor: '#39A2DB',
                      borderColor: '#39A2DB',
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '21px',
                      marginTop: '54px',
                      position: 'relative',
                    }}
                  >
                    Reset Password
                    <div style={{ position: 'absolute', right: 59 }}>
                      <Tooltip
                        title="This will create a temporary password which the user can use to access their account. The user will be forced to reset their password when they log in. Do not use this button unless the user has requested a new password."
                        placement="right"
                      >
                        <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4' }} />
                      </Tooltip>
                    </div>
                  </Button>
                </React.Fragment>
              ) : props.modalState === ResetPasscodeModalStates.ResetPassword ? (
                <React.Fragment>
                  <Typography
                    style={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '25px',
                      textAlign: 'center',
                      color: '#14142B',
                      margin: '10px 0',
                    }}
                  >
                    Password Reset Successfully!
                  </Typography>
                  <img src={ResetPasswordIcon} alt="" />
                  <Typography
                    style={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '22px',
                      textAlign: 'center',
                      color: '#000000',
                      marginTop: '24px',
                    }}
                  >
                    {props.subjectDetails.Email}
                  </Typography>
                  <Box
                    sx={{
                      border: '2px solid #F1F0F5',
                      borderRadius: '8px',
                      width: 351,
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: '18px !important',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 116,
                        borderRight: '2px solid #F1F0F5',
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: 'Work Sans',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '18px',
                          lineHeight: '22px',
                          color: '#14142B',
                        }}
                      >
                        Password
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        padding: '9px 18px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        width: 235,
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: 'Work Sans',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontSize: '18px',
                          lineHeight: '22px',
                          color: '#14142B',
                          marginRight: '20px',
                        }}
                      >
                        {props.tempPasscode}
                      </Typography>
                      <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="Copy special code"
                        onClick={() => {
                          navigator.clipboard.writeText(props.tempPasscode);
                          setSnackBarOpen(true);
                        }}
                        sx={{
                          position: 'absolute',
                          right: 5,
                        }}
                      >
                        <ContentCopyIcon sx={{ width: '22px', height: '22px', fill: '#14142B' }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography
                    style={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '25px',
                      textAlign: 'center',
                      color: '#000000',
                      marginTop: '18px',
                    }}
                  >
                    Note: This is a one time use password
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={props.handleClose}
                    style={{
                      width: '95px',
                      height: '42px',
                      borderRadius: '24px',
                      textTransform: 'capitalize',
                      color: 'white',
                      backgroundColor: '#39A2DB',
                      borderColor: '#39A2DB',
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '21px',
                      marginTop: '38px',
                    }}
                  >
                    Done
                  </Button>
                </React.Fragment>
              ) : (
                <></>
              )}
            </Stack>
          </Stack>
        </Box>
      </Modal>
      {props.isSpinnerModalOpen ? (
        <SpinnerModal
          openSpinnerModal={props.isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={props.spinnerStatus}
          closeCancel={() => {}}
          width="520px"
          height="555px"
        />
      ) : (
        <></>
      )}
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        open={snackBarOpen}
        autoHideDuration={4000}
        onClose={() => {
          setSnackBarOpen(false);
        }}
      >
        <Alert
          onClose={() => {
            setSnackBarOpen(false);
          }}
          severity="success"
          sx={{ width: '100%' }}
        >
          Copied to Clipboard
        </Alert>
      </Snackbar>
    </div>
  );
};
export default ResetPasswordModal;
